'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowRight, Sparkles } from 'lucide-react';

export default function LandingHero() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16" aria-labelledby="hero-heading">
      {/* Background gradients */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-200/30 rounded-full blur-3xl" />
      </div>
      <div className="relative z-10 max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* India's first multilingual badge — right-aligned */}
        <div className="flex justify-end mb-4 fade-in">
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-orange-50 to-green-50 border border-orange-200/60 text-xs sm:text-sm font-medium shadow-sm">
            <span className="text-lg leading-none" aria-hidden="true">🇮🇳</span>
            <span className="text-orange-700 font-semibold">India&apos;s first</span>
            <span className="text-gray-600">multilingual mental wellbeing app</span>
          </div>
        </div>

        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full glass border border-primary/30 text-xs sm:text-sm text-secondary-foreground mb-6 sm:mb-8 fade-in" aria-hidden="true">
          <Sparkles size={14} className="text-primary" />
          <span>AI Emotional Companion — always here for you</span>
        </div>

        {/* Headline */}
        <h1 id="hero-heading" className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight mb-5 sm:mb-6 fade-in">
          <span className="text-foreground">How are you feeling</span>
          <br />
          <span className="gradient-text">today?</span>
        </h1>

        <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed fade-in px-2">
          Uyir is your safe space to say what you really feel.
          No judgment, no advice you didn't ask for. Just a warm presence
          that actually listens — whenever you need it.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-12 sm:mb-16 px-4 sm:px-0">
          <Link
            href="/sign-up-login-screen"
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-2xl gradient-bg text-white font-semibold text-base sm:text-lg hover:opacity-90 active:scale-95 transition-all duration-150 shadow-lg shadow-primary/30 focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
          >
            Start journaling <ArrowRight size={20} aria-hidden="true" />
          </Link>
          <a
            href="#what-is-uyir"
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-2xl glass border border-[var(--glass-border)] text-foreground font-medium text-base sm:text-lg hover:bg-primary/5 transition-all duration-150 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
          >
            See how it works
          </a>
        </div>

        {/* Language support strip */}
        <div className="flex flex-col items-center gap-3 fade-in" aria-label="Supported languages">
          <p className="text-xs text-muted-foreground tracking-wide uppercase font-medium">
            Speak in your language
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { label: 'English', script: 'EN' },
              { label: 'தமிழ்', script: 'TA' },
              { label: 'हिन्दी', script: 'HI' },
              { label: 'ಕನ್ನಡ', script: 'KA' },
              { label: 'తెలుగు', script: 'TE' },
              { label: 'മലയാളം', script: 'ML' },
            ]?.map((lang) => (
              <span
                key={lang?.script}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass border border-[var(--glass-border)] text-xs font-medium text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors duration-200"
              >
                <span className="text-[10px] font-bold text-primary/70 tracking-wider">{lang?.script}</span>
                {lang?.label}
              </span>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}